
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { GamePlan } from '@/types/gamePlanner';
import { offlineGamePlanService } from '@/services/gamePlanner/offlineGamePlanService';
import { useNetworkStatus } from '@/contexts/offline';

interface UseGamePlanOperationsProps {
  gamePlans: GamePlan[];
  setGamePlans: React.Dispatch<React.SetStateAction<GamePlan[]>>;
  templates: GamePlan[];
  setTemplates: React.Dispatch<React.SetStateAction<GamePlan[]>>;
  loadGamePlans: () => void;
  loadTemplates: () => void;
}

export function useGamePlanOperations({
  gamePlans,
  templates,
  loadGamePlans,
  loadTemplates
}: UseGamePlanOperationsProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState<string | null>(null);
  const [confirmTemplateDialogOpen, setConfirmTemplateDialogOpen] = useState(false);
  const [planToConvert, setPlanToConvert] = useState<string | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [planToEdit, setPlanToEdit] = useState<string | null>(null);
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');

  const { toast } = useToast();
  const { isOnline } = useNetworkStatus();
  
  const handleDeletePlan = (id: string) => {
    setPlanToDelete(id);
    setDeleteDialogOpen(true);
  };
  
  const confirmDeletePlan = async () => {
    if (!planToDelete) return null;

    try {
      await offlineGamePlanService.deleteGamePlan(planToDelete);

      const successMessage = isOnline
        ? 'Game plan deleted successfully.'
        : 'Game plan deleted offline and will sync when you\'re back online.';

      toast({
        title: 'Success',
        description: successMessage,
      });

      // Refresh plans
      loadGamePlans();
      loadTemplates();

      return planToDelete;
    } catch (error) {
      console.error('Error deleting game plan:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete the game plan.',
        variant: 'destructive',
      });
      return null;
    } finally {
      setPlanToDelete(null);
      setDeleteDialogOpen(false);
    }
  };
  
  const handleChangeDetails = (id: string) => {
    const plan = [...gamePlans, ...templates].find(p => p.id === id);
    if (plan) {
      setPlanToEdit(id);
      setEditedName(plan.name);
      setEditedDescription(plan.description || '');
      setDetailsDialogOpen(true);
    }
  };
  
  const handleSaveDetails = async () => {
    if (!planToEdit) return null;

    try {
      // Find the plan to update
      const plan = [...gamePlans, ...templates].find(p => p.id === planToEdit);
      if (!plan) {
        throw new Error('Game plan not found');
      }

      // Update the plan with new details
      const updatedPlan = {
        ...plan,
        name: editedName,
        description: editedDescription
      };

      await offlineGamePlanService.updateGamePlan(updatedPlan);

      const successMessage = isOnline
        ? 'Game plan details updated successfully.'
        : 'Game plan details updated offline and will sync when you\'re back online.';

      toast({
        title: 'Success',
        description: successMessage,
      });

      // Refresh plans
      loadGamePlans();
      loadTemplates();

      setDetailsDialogOpen(false);
      setPlanToEdit(null);

      return updatedPlan;
    } catch (error) {
      console.error('Error updating game plan details:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update game plan details.',
        variant: 'destructive',
      });
      return null;
    }
  };
  
  const handleConvertToTemplate = (id: string) => {
    setPlanToConvert(id);
    setConfirmTemplateDialogOpen(true);
  };
  
  const confirmConvertToTemplate = async () => {
    if (!planToConvert) return null;

    try {
      // Find the plan to convert
      const plan = [...gamePlans, ...templates].find(p => p.id === planToConvert);
      if (!plan) {
        throw new Error('Game plan not found');
      }

      // Update the plan to be a template
      const updatedPlan = {
        ...plan,
        is_template: true
      };

      await offlineGamePlanService.updateGamePlan(updatedPlan);

      const successMessage = isOnline
        ? 'Game plan converted to template successfully.'
        : 'Game plan converted to template offline and will sync when you\'re back online.';

      toast({
        title: 'Success',
        description: successMessage,
      });

      // Refresh plans
      loadGamePlans();
      loadTemplates();

      return updatedPlan;
    } catch (error) {
      console.error('Error converting game plan to template:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to convert game plan to template.',
        variant: 'destructive',
      });
      return null;
    } finally {
      setPlanToConvert(null);
      setConfirmTemplateDialogOpen(false);
    }
  };
  
  return {
    deleteDialogOpen,
    setDeleteDialogOpen,
    planToDelete,
    handleDeletePlan,
    confirmDeletePlan,
    
    detailsDialogOpen,
    setDetailsDialogOpen,
    planToEdit,
    editedName,
    setEditedName,
    editedDescription,
    setEditedDescription,
    handleChangeDetails,
    handleSaveDetails,
    
    confirmTemplateDialogOpen,
    setConfirmTemplateDialogOpen,
    planToConvert,
    handleConvertToTemplate,
    confirmConvertToTemplate,
  };
}
